class RcVariantPicker extends HTMLElement {

  constructor() {
    super();
    this.event;
    this.productData = {};
    this.currentVariantId = null;
    this.selectedOptions = {};
    this.optionNames = [];
    this.colorOptionIndex = null;
    this.variantInventory = {};
    this.showToast = false;
  };

  connectedCallback() { this.initialize(); };

  disconnectedCallback() { this.deinitialize(); };

  setCurrentVariantId(variantId){ this.currentVariantId = variantId; };

  setProductData = (data) => {
    if(!data) return;
    const inventoryFromDOM = this.querySelector('script[type="application/json"][data-variant-inventory]');
    this.productData = data;
    this.optionNames = Array.isArray(data.options) ? data.options : [];
    this.colorOptionIndex = this.optionNames.findIndex((name) => {
      if(inventoryFromDOM && inventoryFromDOM.textContent) {
        try {
          this.variantInventory = JSON.parse(inventoryFromDOM.textContent) || {};
        } catch (e) {
          console.error('Error parsing variant inventory JSON', e);
          this.variantInventory = {};
        };
      };
      const lower = (name || '').toLowerCase();
      return lower.includes('color') || lower.includes('colour');
    });
  };

  assignEvents(selectors) {
    this._handlers = this._handlers || new Map();
    selectors.forEach((selector) => {
      const optionIndex = Number(selector.dataset.optionIndex);
      if(Number.isNaN(optionIndex)) return;
      const handler = (event) => {
        const target = event.target;
        const isSelect = target.tagName === 'SELECT';
        if(!isSelect && !target.checked) return;
        const value = target.value;
        if(!value) return;
        this.selectedOptions[optionIndex] = value;
        this.updateOptionAvailability();
        this.updateCurrentVariantFromSelectedOptions();
        this.updateActiveSwatches();
        this.updateUrl(this.currentVariantId);
      };
      this._handlers.set(selector, handler);
      selector.addEventListener('change', handler);
      if(selector.tagName === 'SELECT' && selector.value) {
        this.selectedOptions[optionIndex] = selector.value;
      } else if (selector.type === 'radio' && selector.checked) {
        this.selectedOptions[optionIndex] = selector.value;
      };
    });
  };

  updateActiveSwatches() {
    const radios = this.querySelectorAll('input[type="radio"][data-variant-selector]');
    if(!radios || !radios.length) return;
    radios.forEach((radio) => {
      const label = radio.closest('label');
      if(!label) return;
      if(radio.checked) {
        label.dataset.active = 'true';
      } else {
        label.dataset.active = 'false';
      };
    });
  };

  updateCurrentVariantFromSelectedOptions() {
    if(!this.productData || !Array.isArray(this.productData.variants)) return;
    const variants = this.productData.variants;
    const optionNames = this.optionNames;
    const activeVariant = variants.find((variant) => {
      return optionNames.every((name, index) => {
        const selectedValue = this.selectedOptions[index];
        if(!selectedValue) return true;
        const key = `option${index + 1}`;
        return String(variant[key]) === String(selectedValue);
      });
    });
    if(!activeVariant) return;

    if(this.variantInventory && Object.prototype.hasOwnProperty.call(this.variantInventory, activeVariant.id)) {
      activeVariant.inventory_quantity = this.variantInventory[activeVariant.id];
    };
    this.setCurrentVariantId(activeVariant.id);
    
    // Dispatch custom event
    const customEvent = new CustomEvent(this.event, {
      detail: { data: activeVariant },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);
  };

  updateOptionAvailability() {
    if(!this.productData || !Array.isArray(this.productData.variants)) return;
    const variants = this.productData.variants;
    const selectors = this.getSelectors;

    // Group selectors by option index
    const selectorsByOption = {};
    selectors.forEach((selector) => {
      const optionIndex = Number(selector.dataset.optionIndex);
      if(Number.isNaN(optionIndex)) return;
      if(!selectorsByOption[optionIndex]) {
        selectorsByOption[optionIndex] = [];
      }
      selectorsByOption[optionIndex].push(selector);
    });

    // Process each option index
    Object.keys(selectorsByOption).forEach((optionIndexStr) => {
      const optionIndex = Number(optionIndexStr);
      const keyForThis = `option${optionIndex + 1}`;
      
      // Get all other selected options (excluding current option)
      const otherSelectedOptions = {};
      Object.keys(this.selectedOptions).forEach((idx) => {
        const idxNum = Number(idx);
        if(idxNum !== optionIndex && this.selectedOptions[idxNum] != null) {
          otherSelectedOptions[idxNum] = this.selectedOptions[idxNum];
        }
      });

      // Calculate valid values for this option based on other selections
      const validValues = new Set();
      variants.forEach((variant) => {
        if(!variant.available) return;
        
        // Check if variant matches all other selected options
        let matchesOtherSelections = true;
        Object.keys(otherSelectedOptions).forEach((otherIdx) => {
          const otherKey = `option${Number(otherIdx) + 1}`;
          if(String(variant[otherKey]) !== String(otherSelectedOptions[otherIdx])) {
            matchesOtherSelections = false;
          }
        });
        
        if(!matchesOtherSelections) return;
        
        const value = variant[keyForThis];
        if (value != null) validValues.add(String(value));
      });

      // Update all selectors for this option index (only radio buttons, not SELECT)
      selectorsByOption[optionIndex].forEach((selector) => {
        const isRadio = selector.type === 'radio';
        if(isRadio) {
          // Handle radio button inputs only
          const value = selector.value;
          const enabled = validValues.has(String(value));
          // Using data-disabled since we still want users to be able to click the disabled option for email reminders
          selector.setAttribute('data-disabled', !enabled);
        }
      });
    });
  };

  updateUrl(variantId) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    if(variantId) {
      params.set('variant', variantId)
    } else {
      params.delete('variant');
    }
    
    url.search = params.toString();
    window.history.replaceState({}, '', url.toString());
  };

  get getSelectors() { return Array.from(this.querySelectorAll('[data-variant-selector]')); };

  initialize() {
    const dataFromDOM = this.querySelector('script[type="application/json"][data-variant-data]');
    this.event = window.umbrellaConfig.events.variantChange;
    this.showToast = window.umbrellaConfig && window.umbrellaConfig.showToast;
    if(!dataFromDOM || !dataFromDOM.textContent) return;
    const data = JSON.parse(dataFromDOM.textContent);
    if(!data) return;
    try {
      this.setProductData(data.product);
      this.assignEvents(this.getSelectors);
      // On page load
      this.updateOptionAvailability();
      this.updateActiveSwatches();
    } catch (e) {
      console.error(e);
      if(this.showToast) {
        const msg = window.umbrellaConfig.devMode 
          ? `Source => VariantPicker: ${e.message}` 
          : window.umbrellaConfig.toast_settings.messages.genericError;
        showToast(msg, 'error');
      };
      return;
    };
  };

  deinitialize() {
    if(!this._handlers) return;
    this._handlers.forEach((handler, selector) => {
      selector.removeEventListener('change', handler);
    });
    this._handlers.clear();
  };

};

if(!customElements.get('rc-variant-picker')) customElements.define('rc-variant-picker', RcVariantPicker);